﻿import { Loader2, Receipt, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import type { OrderStatus } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { QueryError } from "@/components/query-error";
import { useOrdersInfiniteQuery } from "@/features/orders/api";
import { ISTANBUL_TZ, useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const STATUS_VARIANTS: Record<
  OrderStatus,
  "success" | "warning" | "secondary" | "destructive" | "outline" | "default"
> = {
  Pending: "warning",
  Paid: "success",
  Cancelled: "destructive",
  Expired: "secondary",
  Refunded: "outline",
  PartiallyRefunded: "warning",
};

const STATUS_FILTERS: ReadonlyArray<OrderStatus | ""> = [
  "",
  "Paid",
  "Pending",
  "Refunded",
  "PartiallyRefunded",
  "Cancelled",
  "Expired",
];

const LIST_DATE: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: ISTANBUL_TZ,
};

export function OrdersPage() {
  return (
    <DashboardPage>
      <OrdersView />
    </DashboardPage>
  );
}

/** Basit debounce — search input'unun her tuşta API'yi dövmesini önler. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function OrdersView() {
  const t = useT("orders");
  const tCommon = useT("common");
  const tStatus = useT("orderStatus");
  const fmt = useFormat();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<OrderStatus | "">("");
  const debouncedSearch = useDebouncedValue(search, 350);

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      status: status || undefined,
    }),
    [debouncedSearch, status],
  );

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
    isRefetching,
  } = useOrdersInfiniteQuery(filters);

  const items = useMemo(
    () => data?.pages.flatMap((p) => p?.items ?? []) ?? [],
    [data],
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  const hasFilters = !!debouncedSearch || !!status;
  const isEmpty = !isLoading && items.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("title")}</h1>
        <p className="text-sm text-muted-foreground">{t("description")}</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("searchPlaceholder")}
            aria-label={t("searchPlaceholder")}
            className="ps-9 pe-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              aria-label={t("clearSearch")}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex overflow-x-auto gap-1 rounded-md border bg-card p-1 sm:w-auto">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setStatus(f)}
              aria-pressed={status === f}
              className={cn(
                "whitespace-nowrap rounded px-3 py-1.5 text-xs font-medium transition",
                status === f
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-muted",
              )}
            >
              {f === "" ? t("filterAll") : tStatus(f)}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <QueryError
          message={t("loadError")}
          onRetry={() => refetch()}
          retrying={isRefetching}
        />
      ) : isEmpty ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
            <Receipt className="h-8 w-8" />
            <div>
              <p className="font-medium text-foreground">
                {hasFilters ? t("noResults") : t("emptyTitle")}
              </p>
              <p className="mt-1 text-sm">
                {hasFilters ? t("noResultsHint") : t("emptyHint")}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="text-xs text-muted-foreground">
            {t("showing", {
              shown: fmt.number(items.length),
              total: fmt.number(totalCount),
            })}
          </div>

          {/* Desktop tablo (sm+) */}
          <Card className="hidden sm:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-start font-medium">{t("columns.order")}</th>
                      <th className="px-4 py-2 text-start font-medium">{t("columns.event")}</th>
                      <th className="px-4 py-2 text-start font-medium">{t("columns.buyer")}</th>
                      <th className="px-4 py-2 text-end font-medium">{t("columns.tickets")}</th>
                      <th className="px-4 py-2 text-end font-medium">{t("columns.amount")}</th>
                      <th className="px-4 py-2 text-start font-medium">{t("columns.status")}</th>
                      <th className="px-4 py-2 text-start font-medium">{t("columns.date")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((o) => (
                      <tr
                        key={o.orderNumber}
                        className="border-t hover:bg-muted/30"
                      >
                        <td className="px-4 py-2 font-mono text-xs">
                          <Link
                            to={`/orders/${o.orderNumber}`}
                            className="hover:underline"
                          >
                            {o.orderNumber}
                          </Link>
                        </td>
                        <td className="px-4 py-2">
                          <div className="font-medium">{o.eventTitle}</div>
                          <div className="text-xs text-muted-foreground">
                            {o.shopSlug}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div>
                            {o.buyerFirstName} {o.buyerLastName}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {o.buyerEmail}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-end tabular-nums">
                          {fmt.number(o.ticketCount)}
                        </td>
                        <td className="px-4 py-2 text-end tabular-nums font-medium">
                          {fmt.money(o.total, o.currency)}
                        </td>
                        <td className="px-4 py-2">
                          <Badge variant={STATUS_VARIANTS[o.status]}>
                            {tStatus(o.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {fmt.dateTime(o.createdAt, LIST_DATE)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Mobile card listesi (<sm) */}
          <div className="space-y-2 sm:hidden">
            {items.map((o) => (
              <Link
                key={o.orderNumber}
                to={`/orders/${o.orderNumber}`}
                className="block rounded-md border bg-card p-3 transition hover:bg-muted/30"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {o.eventTitle}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {o.buyerFirstName} {o.buyerLastName} · {o.buyerEmail}
                    </div>
                  </div>
                  <Badge variant={STATUS_VARIANTS[o.status]}>
                    {tStatus(o.status)}
                  </Badge>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-mono">{o.orderNumber}</span>
                  <span className="tabular-nums font-medium text-foreground">
                    {fmt.money(o.total, o.currency)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{t("ticketCount", { count: o.ticketCount })}</span>
                  <span>{fmt.dateTime(o.createdAt, LIST_DATE)}</span>
                </div>
              </Link>
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {tCommon("loading")}
                  </>
                ) : (
                  t("loadMore")
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
